import { actions, IStore, MARKET_DATA_STORE_NAME } from './store';
import { frontendActions, LocationType } from '@cmctechnology/webinvest-store-frontend';
import { CmcWebApi } from '@cmctechnology/webinvest-api-client-web-trading';
import { Dispatch } from '@reduxjs/toolkit';
import { MARKET_DATA_API_NAMES } from './models/marketDataApiNames';
import { ResourceKey } from '@cmctechnology/webinvest-store-frontend/dist/js/models/subscriptions';
import { FxRateKey } from './types';
import isEqual from 'lodash.isequal';
import { getQuotesUseWs, setQuotesUseWs, subscribeWsQuotes } from './wsActions';

export const fetchFxRates = (fxRates: FxRateKey[]) => {
  return async (dispatch: Dispatch<any>, getState: () => IStore) => {
    try {
      const promises = fxRates.map(async (fxRate) => {
        const isFirst = !getState()[MARKET_DATA_STORE_NAME].fxRates[fxRate.accountNo]?.[fxRate.fromCurrencyCode]?.[fxRate.toCurrencyCode];

        if (fxRate.fromCurrencyCode === fxRate.toCurrencyCode) {
          return { ...fxRate, fxRate: { Bid: 1, Offer: 1, BuyingPower: 1, Unavailable: false, ErrorReason: undefined as any } };
        }

        const request = { CurrencyFrom: fxRate.fromCurrencyCode, CurrencyTo: fxRate.toCurrencyCode, AccountNumber: fxRate.accountNo };
        const response = await frontendActions.webTradingApiCall<CmcWebApi.MarketInformation.ExchangeRateModel1>(
          MARKET_DATA_STORE_NAME,
          MARKET_DATA_API_NAMES.ExchangeRateGet1,
          request,
          dispatch,
          undefined,
          isFirst
        );
        return { ...fxRate, fxRate: response };
      });
      const responses = await Promise.all(promises);
      dispatch<any>(actions.fxRatesFetched(responses));
    } catch {}
  };
};

const FX_RATE_EXPIRY = 30_000;
export const fetchFxRatesByWs = (fxRates: FxRateKey[]) => {
  return (dispatch: Dispatch<any>, getState: () => IStore) => {
    const subs = getState()[MARKET_DATA_STORE_NAME].fxRateSubscriptions;
    /* istanbul ignore else */
    if (!isEqual(fxRates, subs)) {
      dispatch(actions.fxRatesSubscribed(fxRates));
      dispatch(subscribeWsQuotes());
    }
    const expiry = Date.now().valueOf() - FX_RATE_EXPIRY;
    const rates = getState()[MARKET_DATA_STORE_NAME].fxRates;
    const expired = fxRates.filter((fxRate) => (rates[fxRate.accountNo]?.[fxRate.fromCurrencyCode]?.[fxRate.toCurrencyCode].lastUpdated ?? 0) < expiry);
    /* istanbul ignore else */
    if (expired.length > 0) dispatch(fetchFxRates(expired));
  };
};

export const setFxRateUseWs = (value: boolean) => setQuotesUseWs(value);

// eslint-disable-next-line unicorn/consistent-function-scoping
export const onPollFxRates = (subscriptions: ResourceKey[] = []) => {
  const subs = subscriptions.map((key) => JSON.parse(key.toString()) as FxRateKey);
  if (subs.length === 0) return () => {};
  return (dispatch: Dispatch<any>) => {
    const sameSubs = subs.filter((sub) => sub.fromCurrencyCode === sub.toCurrencyCode);
    if (sameSubs.length > 0) dispatch<any>(fetchFxRates(sameSubs));
    const diffSubs = subs.filter((sub) => sub.fromCurrencyCode !== sub.toCurrencyCode);
    /* istanbul ignore else */
    if (diffSubs.length > 0) {
      if (getQuotesUseWs()) {
        dispatch<any>(fetchFxRatesByWs(diffSubs));
      } else {
        dispatch<any>(fetchFxRates(diffSubs));
      }
    }
  };
};

const FX_RATES_POLL_TIMEOUT = 2_000;

export const subscribeFxRates = (location: LocationType, requests: FxRateKey[]) => (dispatch: Dispatch<any>) => {
  dispatch<any>(
    frontendActions.subscribe({
      onPoll: onPollFxRates,
      location,
      apiName: MARKET_DATA_API_NAMES.ExchangeRateGet1,
      storeName: MARKET_DATA_STORE_NAME,
      resourceKeys: requests.map((request) => JSON.stringify(request)),
      pollTimeout: FX_RATES_POLL_TIMEOUT
    })
  );
};

export const unsubscribeFxRates = (location: LocationType, requests: FxRateKey[]) => (dispatch: Dispatch<any>) => {
  dispatch<any>(
    frontendActions.unsubscribe({
      location,
      apiName: MARKET_DATA_API_NAMES.ExchangeRateGet1,
      storeName: MARKET_DATA_STORE_NAME,
      resourceKeys: requests.map((request) => JSON.stringify(request))
    })
  );
};
